import React, { useState, useEffect, useRef } from 'react';

const FileManager = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [dragging, setDragging] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [newName, setNewName] = useState('');
  const fileInput = useRef(null);

  const fetchFiles = () => {
    fetch('/api/admin-files.php?action=list')
      .then((r) => r.ok ? r.json() : { files: [] })
      .then((data) => setFiles(data.files || []))
      .catch(() => setFiles([]));
  };

  useEffect(() => { fetchFiles(); }, []);

  const uploadFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    setMessage('');

    const formData = new FormData();
    for (let i = 0; i < fileList.length; i++) {
      formData.append('files[]', fileList[i]);
    }

    try {
      const res = await fetch('/api/admin-files.php?action=upload', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      if (data.success) {
        setMessage(`${data.count}件アップロードしました`);
        fetchFiles();
      } else {
        setMessage('アップロードに失敗しました: ' + (data.message || ''));
      }
    } catch {
      setMessage('アップロードに失敗しました');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const handleUpload = (e) => uploadFiles(e.target.files);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    uploadFiles(e.dataTransfer.files);
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`${filename} を削除しますか？`)) return;
    try {
      const res = await fetch('/api/admin-files.php?action=delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename }),
      });
      const data = await res.json();
      if (data.success) {
        setMessage('削除しました');
        fetchFiles();
      }
    } catch {
      setMessage('削除に失敗しました');
    }
  };

  const startRename = (name) => {
    setRenaming(name);
    setNewName(name);
  };

  const handleRename = async () => {
    if (!newName || newName === renaming) { setRenaming(null); return; }
    try {
      const res = await fetch('/api/admin-files.php?action=rename', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ oldName: renaming, newName }),
      });
      const data = await res.json();
      if (data.success) {
        setMessage('ファイル名を変更しました');
        fetchFiles();
      } else {
        setMessage(data.message || 'ファイル名の変更に失敗しました');
      }
    } catch {
      setMessage('ファイル名の変更に失敗しました');
    }
    setRenaming(null);
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(window.location.origin + url);
    setMessage('URLをコピーしました');
    setTimeout(() => setMessage(''), 2000);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isImage = (name) => /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(name);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">ファイル管理</h2>
        <div>
          <input
            type="file"
            ref={fileInput}
            onChange={handleUpload}
            multiple
            className="hidden"
          />
          <button
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50"
          >
            {uploading ? 'アップロード中...' : 'ファイルをアップロード'}
          </button>
        </div>
      </div>

      {message && <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm">{message}</div>}

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={`mb-4 border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
          dragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'
        }`}
      >
        <p className={`text-sm ${dragging ? 'text-blue-600 font-bold' : 'text-gray-400'}`}>
          {uploading ? 'アップロード中...' : dragging ? 'ここにドロップしてアップロード' : 'ファイルをここにドラッグ＆ドロップ'}
        </p>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        {files.length === 0 ? (
          <div className="p-8 text-center text-gray-500">ファイルがありません</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-bold text-gray-600">ファイル名</th>
                <th className="text-left px-4 py-3 font-bold text-gray-600 w-24">サイズ</th>
                <th className="text-right px-4 py-3 font-bold text-gray-600">操作</th>
              </tr>
            </thead>
            <tbody>
              {files.map((file, i) => (
                <tr key={i} className="border-b last:border-b-0 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {isImage(file.name) ? (
                        <img src={file.url} alt={file.name} className="w-10 h-10 object-cover rounded border" />
                      ) : (
                        <div className="w-10 h-10 bg-gray-100 rounded border flex items-center justify-center">
                          <span className="text-[10px] font-bold text-gray-500">{file.name.split('.').pop().toUpperCase()}</span>
                        </div>
                      )}
                      {renaming === file.name ? (
                        <form onSubmit={(e) => { e.preventDefault(); handleRename(); }} className="flex items-center gap-2 flex-1 min-w-0">
                          <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus
                            className="flex-1 border border-blue-400 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                          <button type="submit" className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-bold hover:bg-blue-700">OK</button>
                          <button type="button" onClick={() => setRenaming(null)} className="bg-gray-200 text-gray-700 px-2 py-1 rounded text-xs font-bold hover:bg-gray-300">取消</button>
                        </form>
                      ) : (
                        <span className="truncate">{file.name}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{formatSize(file.size)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="flex gap-2 justify-end flex-nowrap">
                      <button onClick={() => startRename(file.name)} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-bold hover:bg-gray-200">
                        名前変更
                      </button>
                      <button onClick={() => copyUrl(file.url)} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-bold hover:bg-gray-200">
                        URL
                      </button>
                      <a href={file.url} target="_blank" rel="noopener noreferrer" className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-bold hover:bg-gray-200">
                        開く
                      </a>
                      <button onClick={() => handleDelete(file.name)} className="bg-red-50 text-red-600 px-2 py-1 rounded text-xs font-bold hover:bg-red-100">
                        削除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default FileManager;
